import { useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { fileUrl, pick } from "@/components/site/Services";
import { ImageUpload } from "./ImageUpload";

const LANGS = ["it", "en", "de", "fr"];
const EMPTY = {
  services: { title: { it: "", en: "", de: "", fr: "" }, desc: { it: "", en: "", de: "", fr: "" }, image: "", order: 0 },
  clients: { name: "", logo: "", url: "", order: 0 },
  portfolio: { caption: "", image: "", order: 0 },
};
const ID = { services: "service_id", clients: "client_id", portfolio: "item_id" };

const Field = ({ label, children }) => (
  <label className="flex flex-col gap-1.5 text-xs text-slate-400">{label}{children}</label>
);

const ServiceFields = ({ form, set, sc }) => {
  const [l, setL] = useState("it");
  const setI18n = (k) => (e) => set({ ...form, [k]: { ...form[k], [l]: e.target.value } });
  return (
    <>
      <div className="flex gap-1.5">
        {LANGS.map((x) => (
          <button key={x} type="button" onClick={() => setL(x)} data-testid={`svc-lang-${x}`} className={`rounded-full px-3 py-1 text-xs font-mono uppercase ${l === x ? "bg-white text-[#050713]" : "border border-white/15 text-slate-300"}`}>{x}{form.title[x] && x !== l ? " ·" : ""}</button>
        ))}
      </div>
      <Field label={`${sc.title} (${l.toUpperCase()})`}><input className="field" value={form.title[l]} onChange={setI18n("title")} required={l === "it"} data-testid="svc-title-input" /></Field>
      <Field label={`${sc.desc} (${l.toUpperCase()})`}><textarea rows={4} className="field resize-none" value={form.desc[l]} onChange={setI18n("desc")} required={l === "it"} data-testid="svc-desc-input" /></Field>
      <Field label={sc.image}><ImageUpload value={form.image} onChange={(image) => set({ ...form, image })} labels={sc} testid="svc-image" /></Field>
    </>
  );
};

const ClientFields = ({ form, set, sc }) => (
  <>
    <Field label={sc.name}><input className="field" value={form.name} onChange={(e) => set({ ...form, name: e.target.value })} required data-testid="cli-name-input" /></Field>
    <Field label={sc.url}><input className="field" type="url" value={form.url} onChange={(e) => set({ ...form, url: e.target.value })} placeholder="https://" data-testid="cli-url-input" /></Field>
    <Field label={sc.logo}><ImageUpload value={form.logo} onChange={(logo) => set({ ...form, logo })} labels={sc} testid="cli-logo" aspect="aspect-[2/1]" /></Field>
  </>
);

const PortfolioFields = ({ form, set, sc }) => (
  <>
    <Field label={sc.image}><ImageUpload value={form.image} onChange={(image) => set({ ...form, image })} labels={sc} testid="pf-image" /></Field>
    <Field label={sc.caption}><input className="field" value={form.caption} onChange={(e) => set({ ...form, caption: e.target.value })} data-testid="pf-caption-input" /></Field>
  </>
);

const FIELDS = { services: ServiceFields, clients: ClientFields, portfolio: PortfolioFields };

const Editor = ({ kind, item, onClose, onSaved, sc }) => {
  const [form, set] = useState(item ? { ...EMPTY[kind], ...item } : EMPTY[kind]);
  const [saving, setSaving] = useState(false);
  const Fields = FIELDS[kind];

  const submit = async (e) => {
    e.preventDefault();
    if (kind === "portfolio" && !form.image) return toast.error(sc.imageRequired);
    setSaving(true);
    try {
      const body = { ...form, order: Number(form.order) || 0 };
      delete body[ID[kind]]; delete body.created_at;
      if (item) await api.put(`/admin/showcase/${kind}/${item[ID[kind]]}`, body);
      else await api.post(`/admin/showcase/${kind}`, body);
      toast.success(sc.saved);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err, "Error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0b0e26] border-white/10 text-white sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid={`showcase-editor-${kind}`}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{item ? sc.edit : sc.add} · {sc[kind]}</DialogTitle>
          <DialogDescription className="text-slate-400">{kind === "services" ? sc.langHint : sc[kind]}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col gap-4">
          <Fields form={form} set={set} sc={sc} />
          <Field label={sc.order}><input className="field" type="number" value={form.order} onChange={(e) => set({ ...form, order: e.target.value })} data-testid="showcase-order-input" /></Field>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-full border border-white/15 px-4 py-2 text-sm text-slate-200" data-testid="showcase-cancel-button">{sc.cancel}</button>
            <button type="submit" disabled={saving} className="btn-chrome rounded-full px-5 py-2 text-sm font-semibold text-[#050713]" data-testid="showcase-save-button">{sc.save}</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Group = ({ kind, items, sc, lang, reload }) => {
  const [editing, setEditing] = useState(undefined);
  const img = (x) => x.image || x.logo;
  const label = (x) => (kind === "services" ? pick(x.title, lang) : kind === "clients" ? x.name : x.caption || "—");
  const del = async (x) => {
    try { await api.delete(`/admin/showcase/${kind}/${x[ID[kind]]}`); toast.success(sc.deleted); reload(); } catch (e) { toast.error(errMsg(e, "Error")); }
  };
  return (
    <div className="bento-card rounded-2xl p-5 md:p-6" data-testid={`showcase-group-${kind}`}>
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-display text-xl font-bold text-white">{sc[kind]} <span className="font-mono text-sm text-slate-400">{items.length}</span></h3>
        <button onClick={() => setEditing(null)} data-testid={`showcase-add-${kind}`} className="btn-chrome rounded-full px-4 py-1.5 text-xs font-semibold text-[#050713] flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> {sc.add}</button>
      </div>
      {items.length === 0 && <p className="mt-4 text-sm text-slate-400">{sc.none}</p>}
      <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((x) => (
          <div key={x[ID[kind]]} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-3" data-testid={`showcase-item-${x[ID[kind]]}`}>
            {img(x) ? <img src={fileUrl(img(x))} alt="" className="w-14 h-14 rounded-lg object-cover bg-white/5" /> : <span className="w-14 h-14 rounded-lg bento-hero" />}
            <p className="min-w-0 flex-1 text-sm text-white truncate">{label(x)}</p>
            <button onClick={() => setEditing(x)} aria-label={sc.edit} data-testid={`showcase-edit-${x[ID[kind]]}`} className="p-2 text-slate-300 hover:text-white"><Pencil className="w-4 h-4" /></button>
            <button onClick={() => del(x)} aria-label={sc.remove} data-testid={`showcase-delete-${x[ID[kind]]}`} className="p-2 text-rose-300 hover:text-rose-200"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      {editing !== undefined && <Editor kind={kind} item={editing} sc={sc} onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); reload(); }} />}
    </div>
  );
};

export const ShowcaseTab = ({ data, reload }) => {
  const { t, lang } = useLang();
  const sc = t.admin.showcase;
  return (
    <div className="mt-6 space-y-4">
      {Object.keys(EMPTY).map((k) => <Group key={k} kind={k} items={data[k] ?? []} sc={sc} lang={lang} reload={reload} />)}
    </div>
  );
};
